import type { Cell } from "./types";

/** Shift a shape so its minimum row and column are both 0. */
export function normalize(cells: Cell[]): Cell[] {
  let minR = Infinity, minC = Infinity;
  for (const [r, c] of cells) { if (r < minR) minR = r; if (c < minC) minC = c; }
  return cells.map(([r, c]): Cell => [r - minR, c - minC]);
}

/** Rotate a shape 90° clockwise (and re-normalize). */
export function rotate(cells: Cell[]): Cell[] {
  return normalize(cells.map(([r, c]): Cell => [c, -r]));
}

/** Bounding box (height, width) of a normalized shape. */
export function bounding(cells: Cell[]): { h: number; w: number } {
  let h = 0, w = 0;
  for (const [r, c] of cells) { if (r + 1 > h) h = r + 1; if (c + 1 > w) w = c + 1; }
  return { h, w };
}

/** True if two shapes cover exactly the same set of cells. */
export function cellsEqual(a: Cell[], b: Cell[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a.map(([r, c]) => r + "," + c));
  return b.every(([r, c]) => sa.has(r + "," + c));
}

/**
 * Euclidean radius-coverage test, shared by the game and the solver so the
 * player's "watered" cells and the solver's covered demands agree exactly.
 * A facility at (ar, ac) covers (br, bc) when their distance is within radius.
 * The 1e-9 slack keeps cells exactly on the rim (e.g. a 2.5 radius reaching a
 * (2, 1) offset, √5 < 2.5) robust against floating-point rounding.
 */
export function withinRadius(ar: number, ac: number, br: number, bc: number, radius: number): boolean {
  const dr = ar - br, dc = ac - bc;
  return dr * dr + dc * dc <= radius * radius + 1e-9;
}

/** The distinct rotations of a shape (1, 2, or 4 of them). */
export function uniqueRotations(cells: Cell[]): Cell[][] {
  const rots: Cell[][] = [];
  let cur = normalize(cells.slice());
  for (let i = 0; i < 4; i++) {
    if (!rots.some((r) => cellsEqual(r, cur))) rots.push(cur);
    cur = rotate(cur);
  }
  return rots;
}
